import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '@/App';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  FileText, 
  AlertTriangle, 
  Bell, 
  BarChart3, 
  ArrowRight,
  TrendingUp,
  MapPin,
  Clock,
  Home,
  Train,
  ShoppingBag,
  Building2,
  Leaf,
  FileWarning
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      
      // First seed data if needed
      try {
        await axios.post(`${API}/seed-data`);
      } catch (e) {
        // Ignore - data may already be seeded
      }
      
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar datos' : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadge = (score) => {
    if (score >= 70) return <Badge className="risk-high">{language === 'es' ? 'Alto' : 'High'}</Badge>;
    if (score >= 40) return <Badge className="risk-medium">{language === 'es' ? 'Medio' : 'Medium'}</Badge>;
    return <Badge className="risk-low">{language === 'es' ? 'Bajo' : 'Low'}</Badge>;
  };

  const getJurisdictionLabel = (jurisdiction) => {
    const labels = {
      federal: language === 'es' ? 'Federal' : 'Federal',
      state: language === 'es' ? 'Estatal' : 'State',
      local: language === 'es' ? 'Local' : 'Local'
    };
    return labels[jurisdiction] || jurisdiction;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {language === 'es' ? 'Bienvenido de nuevo' : 'Welcome back'}, {user?.organization_name || user?.company_name || 'User'}
          </h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {language === 'es' ? 'Monitoreando' : 'Monitoring'}: NYC + {stats?.user_state || 'NY'} + Federal
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="deed-theft-shortcut-btn">
            <Link to="/deed-theft">
              <FileWarning className="mr-2 h-4 w-4 text-accent" />
              {language === 'es' ? 'Robo de Escrituras' : 'Deed Theft'}
            </Link>
          </Button>
          <Button asChild data-testid="view-all-policies-btn">
            <Link to="/policies">
              {language === 'es' ? 'Ver Políticas' : 'View Policies'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="transition-all hover:shadow-lg hover:border-primary/20" data-testid="stat-total-policies">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Total de Políticas' : 'Total Policies'}
                </p>
                <p className="text-3xl font-bold mt-1">{stats?.total_policies || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-all hover:shadow-lg hover:border-primary/20" data-testid="stat-state-policies">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Específicas del Estado' : 'State-Specific'}
                </p>
                <p className="text-3xl font-bold mt-1">{stats?.state_policies || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-info/10 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-info" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-all hover:shadow-lg hover:border-accent/20" data-testid="stat-high-risk">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Alto Riesgo' : 'High Risk'}
                </p>
                <p className="text-3xl font-bold mt-1 text-accent">{stats?.high_risk_policies || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-all hover:shadow-lg hover:border-warning/20" data-testid="stat-unread-alerts">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Alertas Sin Leer' : 'Unread Alerts'}
                </p>
                <p className="text-3xl font-bold mt-1">{stats?.unread_alerts || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                <Bell className="h-6 w-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Borough Breakdown */}
      <Card data-testid="borough-breakdown-card">
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            {language === 'es' ? 'Políticas por Distrito' : language === 'ht' ? 'Règleman pa Distri' : 'Policies by Borough'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {['Bronx', 'Brooklyn', 'Manhattan', 'Queens', 'Staten Island'].map((borough) => (
              <Link
                key={borough}
                to={`/policies?borough=${borough}`}
                className="block group"
                data-testid={`borough-${borough.toLowerCase().replace(' ', '-')}`}
              >
                <div className="bg-muted/30 rounded-lg p-4 text-center transition-all hover:bg-primary/10 hover:shadow-md group-hover:border-primary/30 border border-transparent">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2 group-hover:bg-primary/20 transition-colors">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <p className="font-medium text-sm">{borough}</p>
                  <p className="text-2xl font-bold mt-1 text-primary">
                    {stats?.borough_stats?.[borough] || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'es' ? 'políticas' : language === 'ht' ? 'règleman' : 'policies'}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      {stats?.category_stats && (
        <Card data-testid="category-breakdown-card">
          <CardHeader>
            <CardTitle className="text-xl font-semibold">
              {language === 'es' ? 'Políticas por Categoría' : language === 'ht' ? 'Règleman pa Kategori' : 'Policies by Category'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { id: 'housing', icon: Home, label: language === 'es' ? 'Vivienda' : language === 'ht' ? 'Lojman' : 'Housing', color: 'text-blue-600 bg-blue-100' },
                { id: 'transportation', icon: Train, label: language === 'es' ? 'Transporte' : language === 'ht' ? 'Transpò' : 'Transportation', color: 'text-purple-600 bg-purple-100' },
                { id: 'food_access', icon: ShoppingBag, label: language === 'es' ? 'Alimentos' : language === 'ht' ? 'Manje' : 'Food Access', color: 'text-orange-600 bg-orange-100' },
                { id: 'small_business', icon: Building2, label: language === 'es' ? 'Negocios' : language === 'ht' ? 'Ti Biznis' : 'Small Business', color: 'text-green-600 bg-green-100' },
                { id: 'development', icon: TrendingUp, label: language === 'es' ? 'Desarrollo' : language === 'ht' ? 'Devlopman' : 'Development', color: 'text-cyan-600 bg-cyan-100' },
                { id: 'environmental', icon: Leaf, label: language === 'es' ? 'Ambiental' : language === 'ht' ? 'Anviwònman' : 'Environmental', color: 'text-emerald-600 bg-emerald-100' }
              ].map((cat) => {
                const Icon = cat.icon;
                return (
                  <Link
                    key={cat.id}
                    to={`/policies?category=${cat.id}`}
                    className="block group"
                    data-testid={`category-${cat.id}`}
                  >
                    <div className="rounded-lg p-3 border border-border/60 transition-all hover:border-primary/30 hover:shadow-md">
                      <div className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center mb-2`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <p className="text-xs font-medium text-muted-foreground">{cat.label}</p>
                      <p className="text-xl font-bold">{stats.category_stats[cat.id] || 0}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Policies */}
      <Card data-testid="recent-policies-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {language === 'es' ? 'Cambios de Política Recientes' : 'Recent Policy Changes'}
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/policies">
              {language === 'es' ? 'Ver Todo' : 'View All'}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {stats?.recent_policies && stats.recent_policies.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_policies.map((policy, index) => (
                <Link
                  key={policy.id}
                  to={`/policies/${policy.id}`}
                  className="block group"
                  data-testid={`recent-policy-${index}`}
                >
                  <div className="flex items-start gap-4 p-4 rounded-lg border border-border/60 transition-all hover:border-primary/30 hover:shadow-md hover:-translate-y-0.5">
                    <div className={`w-1 h-full min-h-[60px] rounded-full ${
                      policy.risk_score >= 70 ? 'bg-accent' : 
                      policy.risk_score >= 40 ? 'bg-warning' : 'bg-success'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium group-hover:text-primary transition-colors line-clamp-1">
                            {policy.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {language === 'es' && policy.plain_language_summary_es 
                              ? policy.plain_language_summary_es 
                              : policy.plain_language_summary || policy.description}
                          </p>
                        </div>
                        {getRiskBadge(policy.risk_score)}
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {getJurisdictionLabel(policy.jurisdiction)}
                          {policy.state && ` - ${policy.state}`}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {policy.status === 'pending' 
                            ? (language === 'es' ? 'Pendiente' : 'Pending')
                            : (language === 'es' ? 'Activo' : 'Active')
                          }
                        </span>
                        <span className="flex items-center gap-1">
                          <BarChart3 className="h-3 w-3" />
                          {language === 'es' ? 'Riesgo' : 'Risk'}: {policy.risk_score}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{language === 'es' ? 'No hay políticas recientes' : 'No recent policies'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="transition-all hover:shadow-lg hover:border-accent/20 cursor-pointer group" data-testid="quick-action-deed-theft">
          <Link to="/deed-theft">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center transition-colors group-hover:bg-accent/20">
                  <FileWarning className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-medium group-hover:text-accent transition-colors">
                    {language === 'es' ? 'Alertas de Escrituras' : 'Deed Theft Alerts'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Protege tu propiedad' : 'Protect your property'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Link>
        </Card>

        <Card className="transition-all hover:shadow-lg hover:border-primary/20 cursor-pointer group" data-testid="quick-action-risk">
          <Link to="/risk">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium group-hover:text-primary transition-colors">
                    {language === 'es' ? 'Ver Evaluación de Riesgos' : 'View Risk Assessment'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Analiza el impacto comunitario' : 'Analyze community impact'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Link>
        </Card>

        <Card className="transition-all hover:shadow-lg hover:border-primary/20 cursor-pointer group" data-testid="quick-action-comments">
          <Link to="/comments">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium group-hover:text-primary transition-colors">
                    {language === 'es' ? 'Generar Comentario' : 'Generate Comment'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Crea un comentario público' : 'Create a public comment'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Link>
        </Card>

        <Card className="transition-all hover:shadow-lg hover:border-primary/20 cursor-pointer group" data-testid="quick-action-alerts">
          <Link to="/alerts">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                  <Bell className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium group-hover:text-primary transition-colors">
                    {language === 'es' ? 'Configurar Alertas' : 'Configure Alerts'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Gestiona tus preferencias' : 'Manage your preferences'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Link>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
